import { useState } from 'react';
import { searchProcessStreetWorkflowRuns, getWorkflowRunFormFields } from '../api/suggestionService';
import { toast } from 'react-toastify';

export const useProcessStreet = (formData, setFormData, setErrors, dependencies) => {
    const { 
        handleMergePdfs, 
        mailroomConfigs, 
        validateSendingOptionWithSendToOptions, 
        setMergedPdfUrl, 
        setMergedPdfName, 
        handleInputChange,
        replaceFields,
        setEventDateSource
    } = dependencies;

    const [workflowRuns, setWorkflowRuns] = useState([]);
    const [isLoadingWorkflowRuns, setIsLoadingWorkflowRuns] = useState(false);
    const [showWorkflowRunSuggestions, setShowWorkflowRunSuggestions] = useState(false);
    const [workflowRunInput, setWorkflowRunInput] = useState('');
    const [selectedWorkflowRunSuggestionIndex, setSelectedWorkflowRunSuggestionIndex] = useState(-1);
    const [processStreetFormFields, setProcessStreetFormFields] = useState(null);

    const fetchWorkflowRunsForPlan = async (planNumber) => {
        if (!planNumber || !formData.preload_from_process_street) return;
        setIsLoadingWorkflowRuns(true);
        try {
            const response = await searchProcessStreetWorkflowRuns(planNumber);
            setWorkflowRuns(response.data || []);
        } catch (error) {
            console.error('Error fetching workflow runs:', error);
            toast.error('Failed to fetch workflow runs');
            setWorkflowRuns([]);
        } finally {
            setIsLoadingWorkflowRuns(false);
        }
    };

    const handleWorkflowRunSelect = async (workflowRunId, workflowRunName) => {
        try {
            handleInputChange('process_street_workflow_run', workflowRunId);
            setWorkflowRunInput(workflowRunName);
            setShowWorkflowRunSuggestions(false);
            setErrors(prev => ({ ...prev, process_street_workflow_run: false }));

            const response = await getWorkflowRunFormFields(workflowRunId);

            if (response.status && response.data && response.data.flat) {
                const formFields = response.data.flat;

                setProcessStreetFormFields(formFields);

                setMergedPdfUrl(null);
                setMergedPdfName(null);

                if (formFields.Upload_final_meeting_minutes?.value) {
                    const attachmentUrl = formFields.Upload_final_meeting_minutes.value;
                    await handleMergePdfs(null, attachmentUrl);
                }

                const rawDateField = formFields['Date_and_time_of_meeting_(MUST_include_time)']?.value;
                const rawLocation = formFields.Location_of_meeting?.value;

                let parsedEvent = null;
                if (rawDateField) {
                    const [datePart, timePart] = rawDateField.split('T');
                    const [year, month, day] = (datePart || '').split('-');
                    const [hours, minutes] = (timePart || '00:00').split(':');
                    parsedEvent = `${year}-${month}-${day}T${hours}:${minutes}`;
                    setEventDateSource?.('ps');
                }

                setFormData(prevData => ({
                    ...prevData,
                    ...(rawLocation ? { meetingLocation: rawLocation } : {}),
                    mergeFieldsData: {
                        ...(prevData.mergeFieldsData || {}),
                        ...(parsedEvent ? { event: parsedEvent } : {}),
                        ...(rawLocation ? { meeting_location: rawLocation } : {})
                    }
                }));

                const sendToField = formFields['Do_you_wish_to_issue_correspondence_to_all_owners_notifying_them_of_the_meeting_date?'];
                if (sendToField?.value) {
                    const value = sendToField.value;
                    if (value && value.toLowerCase() === 'yes') {
                        setFormData(prevData => ({ ...prevData, sendTo: 'owners' }));
                        setErrors(prev => ({ ...prev, sendTo: false }));

                        if (formData.sending && mailroomConfigs && mailroomConfigs.length > 0) {
                            const selectedConfig = mailroomConfigs.find(config => config.send_category === formData.sending);
                            if (selectedConfig) {
                                validateSendingOptionWithSendToOptions(selectedConfig.id, 'owners', formData.lotsUserTypes);
                            }
                        }
                    }
                }
            } else {
                console.error('Failed to fetch workflow form fields');
                setProcessStreetFormFields(null);
            }
        } catch (error) {
            console.error('Error handling workflow run selection:', error);
            setProcessStreetFormFields(null);
        }
    };

    return {
        workflowRuns,
        setWorkflowRuns,
        isLoadingWorkflowRuns,
        setIsLoadingWorkflowRuns,
        showWorkflowRunSuggestions,
        setShowWorkflowRunSuggestions,
        workflowRunInput,
        setWorkflowRunInput,
        selectedWorkflowRunSuggestionIndex,
        setSelectedWorkflowRunSuggestionIndex,
        processStreetFormFields,
        setProcessStreetFormFields,
        fetchWorkflowRunsForPlan,
        handleWorkflowRunSelect
    };
};
